"use client";

import React, { useCallback, useEffect, useRef } from "react";
import posthog from "posthog-js";
import { useTheme } from "@/lib/once-ui";
import styles from "./ThemeToggle.module.scss";

const RAY_ANGLES = [0, 45, 90, 135, 180, 225, 270, 315];

/**
 * Icon pill in the nav cluster: a sun in light theme that morphs into a
 * crescent moon in dark (rays retract, the core grows, a mask circle slides
 * in to carve the crescent — choreography lives in the module scss).
 * Switching runs a circular view-transition wipe from the button itself;
 * `T` toggles globally.
 */
const MOON_MASK_ID = "oi-theme-toggle-moon";
export const ThemeToggle: React.FC = () => {
  const { resolvedTheme, setTheme } = useTheme();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const nextTheme = resolvedTheme === "light" ? "dark" : "light";

  const toggle = useCallback(() => {
    const next = document.documentElement.getAttribute("data-theme") === "light" ? "dark" : "light";
    posthog.capture("theme_toggled", { theme: next });
    const apply = () => {
      // set synchronously so the view transition captures the new frame
      document.documentElement.setAttribute("data-theme", next);
      setTheme(next);
    };

    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!document.startViewTransition || reduceMotion) {
      apply();
      return;
    }

    const rect = buttonRef.current?.getBoundingClientRect();
    const x = rect ? rect.left + rect.width / 2 : window.innerWidth / 2;
    const y = rect ? rect.top + rect.height / 2 : 0;
    const radius = Math.hypot(
      Math.max(x, window.innerWidth - x),
      Math.max(y, window.innerHeight - y),
    );

    const transition = document.startViewTransition(apply);
    transition.ready
      .then(() => {
        document.documentElement.animate(
          {
            clipPath: [`circle(0px at ${x}px ${y}px)`, `circle(${radius}px at ${x}px ${y}px)`],
          },
          {
            duration: 480,
            easing: "cubic-bezier(0.2, 0.8, 0.2, 1)",
            pseudoElement: "::view-transition-new(root)",
          },
        );
      })
      // rapid toggles skip the pending transition; the theme is applied either way
      .catch(() => {});
  }, [setTheme]);

  // Global shortcut: T flips the theme (hinted in the footer)
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "t" && event.key !== "T") return;
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable)
      ) {
        return;
      }
      toggle();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [toggle]);

  return (
    <button
      ref={buttonRef}
      type="button"
      className={`oi-pill is-icon ${styles.toggle}`}
      onClick={toggle}
      aria-label={`Switch to ${nextTheme} theme (T)`}
      title="Theme (T)"
      suppressHydrationWarning
    >
      <svg
        className={styles.icon}
        viewBox="0 0 14 14"
        width="14"
        height="14"
        aria-hidden="true"
        focusable="false"
      >
        <mask id={MOON_MASK_ID} maskUnits="userSpaceOnUse" x="-6" y="-6" width="26" height="26">
          <rect x="-6" y="-6" width="26" height="26" fill="white" />
          <circle className={styles.maskDot} cx="9.75" cy="4.25" r="4.5" fill="black" />
        </mask>
        <g className={styles.rays}>
          {RAY_ANGLES.map((angle) => (
            <line key={angle} x1="7" y1="0.75" x2="7" y2="2.5" transform={`rotate(${angle} 7 7)`} />
          ))}
        </g>
        <g mask={`url(#${MOON_MASK_ID})`}>
          <circle className={styles.core} cx="7" cy="7" r="2.75" />
        </g>
      </svg>
    </button>
  );
};
